// Viewport3D palettes, classification categories, unit table & analysis-mode GLSL shaders.

export const HIGHLIGHT_COLORS = { rooftop: 0x0a84ff, facade: 0xff9f0a, ground: 0x34c759, interior: 0x00c7be };
// Active-ring theme colors for the hover overlay (match RingsOrbit / theme.pillar)
export const RING_HEX = { locations: 0x0a84ff, systems: 0xff9f0a, crops: 0x34c759, resources: 0x00c7be };
export const RING_CSS = { locations: "#51aafe", systems: "#f88528", crops: "#71d85e", resources: "#3bbbc2" };
// Passive face-identify popup dot colors by surface kind (compass / roof / ground).
export const IDENTIFY_HEX = { facade: "#51aafe", roof: "#34c759", ground: "#8e8e93", under: "#c7c7cc" };

// Manual surface-classification palette. `bucket` drives the engine override (which gross-area
// bucket the tagged area is moved into); "auto" is the eraser and "exterior" keeps auto detection.
export const CLASSIFY_CATS = [
  { id: "ground", label: "Ground", hex: "#8e8e93" },
  { id: "roof", label: "Roof", hex: "#34c759" },
  { id: "podium", label: "Podium", hex: "#ff9f0a" },
  { id: "interior", label: "Interior", hex: "#5e5ce6" },
  { id: "exterior", label: "Exterior", hex: "#51aafe" },
  { id: "opaque", label: "Opaque", hex: "#636366" },
  { id: "glazed", label: "Glazed", hex: "#64d2ff" },
];
// Base surface types (drive the gross-area bucket override). One per surface.
export const BASE_CATS = CLASSIFY_CATS.filter((c) => !["opaque", "glazed"].includes(c.id));
// Material/transparency tags are an INDEPENDENT layer on top of a base type (a surface can be
// e.g. Exterior + Glazed at once); they carry no gross-area bucket effect; "glazed" renders see-through.
export const MATERIAL_CATS = ["opaque", "glazed"];
export const MATERIAL_DEFS = CLASSIFY_CATS.filter((c) => MATERIAL_CATS.includes(c.id));
// Map an auto-detected orientation bucket to a sensible default base type (used when a material
// is applied to an untyped surface so it still carries a base category).
export const BUCKET_TO_BASE = { roof: "roof", base: "ground", N: "exterior", S: "exterior", E: "exterior", W: "exterior" };
export const CLASSIFY_HEX = Object.fromEntries(CLASSIFY_CATS.map((c) => [c.id, c.hex]));
export const ANALYSIS_TITLE = { locations: "Solar Exposure", systems: "Structural Fit", crops: "Crop Survivability", resources: "Placement Zones" };
// Meters-per-unit keyed by rhino3dm's UnitSystem enum (NOT the legacy OpenNURBS ON enum):
// Microns1 Mm2 Cm3 M4 Km5 µin6 Mils7 In8 Ft9 Mi10 Å12 Nm13 Dm14 Dam15 Hm16 Mm(mega)17 Gm18 Yd19
export const UNIT_TO_M = { 0: 1, 1: 1e-6, 2: 0.001, 3: 0.01, 4: 1, 5: 1000, 6: 2.54e-8, 7: 2.54e-5, 8: 0.0254, 9: 0.3048, 10: 1609.344, 12: 1e-10, 13: 1e-9, 14: 0.1, 15: 10, 16: 100, 17: 1e6, 18: 1e9, 19: 0.9144 };
export const MODEL_EXTS = ["3dm", "obj", "glb", "gltf"];

// ---- Ring-synced viability heatmap shader (Analysis Mode) ----
// A per-vertex solar-exposure attribute (aExposure, baked on the CPU when the season /
// north / site changes) is remapped per active ring in the fragment shader. Threshold
// sliders only touch uniforms → 60fps scrubbing with no re-bake.
export const HEAT_VERT = `
  attribute float aExposure;
  varying float vExp;
  varying vec3 vN;
  void main() {
    vExp = aExposure;
    vN = normalize(mat3(modelMatrix) * normal);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
export const HEAT_FRAG = `
  precision highp float;
  uniform vec3 uRingColor;
  uniform vec3 uLow;
  uniform int  uMode;          // 0 loc · 1 sys · 2 crop · 3 res
  uniform float uDliSite;
  uniform float uDliThreshold;
  uniform float uAreaThreshold;
  uniform float uTime;
  varying float vExp;
  varying vec3 vN;
  void main() {
    float flatv = clamp(vN.y, 0.0, 1.0);
    float viab;
    if (uMode == 0) {
      // LOCATION — raw solar viability of the surface
      viab = smoothstep(0.05, 0.9, vExp);
    } else if (uMode == 1) {
      // SYSTEMS — needs sun + a workable (flatter) surface; area slider raises the bar
      float strict = clamp(uAreaThreshold / 200.0, 0.0, 1.0);
      viab = clamp(vExp * 0.6 + flatv * 0.4 - strict * 0.35, 0.0, 1.0);
      viab = smoothstep(0.1, 0.85, viab);
    } else if (uMode == 2) {
      // CROPS — does the surface's received light clear the crop's DLI need?
      float faceDli = uDliSite * vExp;
      float ratio = faceDli / max(uDliThreshold, 1.0);
      viab = smoothstep(0.35, 1.0, ratio) * (1.0 - smoothstep(1.8, 2.6, ratio) * 0.6);
    } else {
      // RESOURCES — flat, accessible surfaces suit equipment placement
      viab = smoothstep(0.5, 0.98, flatv);
    }
    viab = clamp(viab, 0.0, 1.0);
    vec3 col = mix(uLow, uRingColor, viab);
    float b = fract(viab * 5.0);
    float band = smoothstep(0.44, 0.5, b) * smoothstep(0.56, 0.5, b); // iso-contour lines
    col = mix(col, col * 1.28, band * 0.4);
    float shade = 0.72 + 0.28 * max(0.0, dot(normalize(vN), normalize(vec3(0.4, 0.85, 0.35))));
    col *= shade;
    float pulse = 0.92 + 0.08 * sin(uTime * 2.0);
    col *= mix(1.0, pulse, viab); // high-viability zones breathe gently
    gl_FragColor = vec4(col, 1.0);
  }
`;

// World-space Y extent of the building-layer meshes (excludes context) — used to
// distinguish Ground-Level from Rooftop for up-facing faces (positional raycasting).
